import { DATE_RANGE_PRESETS, type DateRangePresetValue } from "./date-range-presets.ts";

export const GRANULARITIES = ["hour", "day", "week", "month", "year"] as const;
export type InboxAnalyticsGranularity = (typeof GRANULARITIES)[number];
export type InboxAnalyticsSort = "inbox" | string;
export type InboxAnalyticsDirection = "asc" | "desc";
export type InboxAnalyticsPeriod = DateRangePresetValue | "custom";

export type InboxAnalyticsUrlState = {
  from: string;
  to: string;
  granularity: InboxAnalyticsGranularity;
  sort: InboxAnalyticsSort;
  direction: InboxAnalyticsDirection;
  period: InboxAnalyticsPeriod;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DEFAULT_GRANULARITY: InboxAnalyticsGranularity = "day";
const DEFAULT_SORT: InboxAnalyticsSort = "inbox";
const DEFAULT_DIRECTION: InboxAnalyticsDirection = "asc";
const PRESET_DAYS: Record<DateRangePresetValue, number> = {
  "24h": 0,
  "7d": 6,
  "30d": 29,
};

function validDate(value: string | null): value is string {
  if (!value || !ISO_DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function validSort(value: string | null): value is InboxAnalyticsSort {
  if (value === "inbox") return true;
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && value === date.toISOString();
}

export function defaultInboxAnalyticsUrlState(now = new Date()): InboxAnalyticsUrlState {
  const range = inboxAnalyticsDateRangeForPreset("30d", now);
  return {
    ...range,
    granularity: DEFAULT_GRANULARITY,
    period: "30d",
    sort: DEFAULT_SORT,
    direction: DEFAULT_DIRECTION,
  };
}

export function inboxAnalyticsDateRangeForPreset(
  preset: DateRangePresetValue,
  now = new Date(),
): Pick<InboxAnalyticsUrlState, "from" | "to"> {
  const end = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - PRESET_DAYS[preset]);
  return { from: formatDate(start), to: formatDate(end) };
}

export function readInboxAnalyticsUrlState(params: URLSearchParams, now = new Date()): InboxAnalyticsUrlState {
  const fallback = defaultInboxAnalyticsUrlState(now);
  const granularity = params.get("granularity");
  const direction = params.get("direction");
  const requestedPeriod = params.get("period");
  const preset = DATE_RANGE_PRESETS.some(({ value }) => value === requestedPeriod)
    ? requestedPeriod as DateRangePresetValue
    : undefined;
  const from = params.get("from");
  const to = params.get("to");
  if (preset) {
    return {
      ...inboxAnalyticsDateRangeForPreset(preset, now),
      period: preset,
      granularity: GRANULARITIES.includes(granularity as InboxAnalyticsGranularity)
        ? granularity as InboxAnalyticsGranularity
        : fallback.granularity,
      sort: validSort(params.get("sort")) ? params.get("sort")! : fallback.sort,
      direction: direction === "desc" || direction === "asc" ? direction : fallback.direction,
    };
  }
  if (validDate(from) && validDate(to)) {
    return {
      from,
      to,
      period: "custom",
      granularity: GRANULARITIES.includes(granularity as InboxAnalyticsGranularity)
        ? granularity as InboxAnalyticsGranularity
        : fallback.granularity,
      sort: validSort(params.get("sort")) ? params.get("sort")! : fallback.sort,
      direction: direction === "desc" || direction === "asc" ? direction : fallback.direction,
    };
  }
  return {
    from: fallback.from,
    to: fallback.to,
    period: fallback.period,
    granularity: GRANULARITIES.includes(granularity as InboxAnalyticsGranularity)
      ? granularity as InboxAnalyticsGranularity
      : fallback.granularity,
    sort: validSort(params.get("sort")) ? params.get("sort")! : fallback.sort,
    direction: direction === "desc" || direction === "asc" ? direction : fallback.direction,
  };
}

export function updateInboxAnalyticsUrl(
  current: URLSearchParams,
  update: Partial<InboxAnalyticsUrlState>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of ["from", "to", "granularity", "sort", "direction", "period"] as const) {
    const value = update[key];
    if (value) next.set(key, value);
    else if (value === "") next.delete(key);
  }
  if (update.period && update.period !== "custom") {
    next.delete("from");
    next.delete("to");
  }
  return next;
}

export function exclusiveEndDate(inclusiveEnd: string): string {
  const date = new Date(`${inclusiveEnd}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) throw new Error("Invalid analytics end date");
  date.setUTCDate(date.getUTCDate() + 1);
  return formatDate(date);
}
